'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';


interface Message {
  id: number;
  type: 'user' | 'bot';
  content: string;
  timestamp: string;
}

interface PerformanceMetric {
  label: string;
  value: string;
  icon: string;
  color: string;
}

const ChatbotDemo = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);

  const demoConversations: Message[] = [
    { id: 1, type: 'user', content: 'I need to return a product I purchased last week', timestamp: '10:23 AM' },
    { id: 2, type: 'bot', content: 'I can help you with that return! To get started, could you please provide your order number? You can find it in your confirmation email.', timestamp: '10:23 AM' },
    { id: 3, type: 'user', content: 'Order #RL-2024-8472', timestamp: '10:24 AM' },
    { id: 4, type: 'bot', content: 'Thank you! I found your order for the Premium Wireless Headphones purchased on January 5th. I can see you\'re within our 30-day return window. What\'s the reason for the return?', timestamp: '10:24 AM' },
    { id: 5, type: 'user', content: 'The sound quality isn\'t what I expected', timestamp: '10:25 AM' },
    { id: 6, type: 'bot', content: 'I understand. I\'ve initiated a return authorization for you. You\'ll receive a prepaid shipping label via email within 5 minutes. Your refund will be processed within 3-5 business days after we receive the item. Is there anything else I can help you with?', timestamp: '10:25 AM' }
  ];

  const performanceMetrics: PerformanceMetric[] = [
    { label: 'Avg Response Time', value: '2.3s', icon: 'ClockIcon', color: 'text-success' },
    { label: 'Resolution Rate', value: '94%', icon: 'CheckCircleIcon', color: 'text-accent' },
    { label: 'Customer Satisfaction', value: '98%', icon: 'FaceSmileIcon', color: 'text-primary' },
    { label: 'Cost per Ticket', value: '$0.12', icon: 'CurrencyDollarIcon', color: 'text-success' }
  ];

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex < demoConversations.length) {
        const currentMessage = demoConversations[currentIndex];
        if (currentMessage.type === 'bot') {
          setIsTyping(true);
          setTimeout(() => {
            setMessages(prev => [...prev, currentMessage]);
            setIsTyping(false);
          }, 1500);
        } else {
          setMessages(prev => [...prev, currentMessage]);
        }
        currentIndex++;
      } else {
        clearInterval(interval);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [isHydrated]);

  if (!isHydrated) {
    return (
      <section className="py-20 lg:py-32 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-muted rounded w-3/4 mx-auto" />
            <div className="h-96 bg-muted rounded-2xl" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 lg:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full mb-6">
            <Icon name="ChatBubbleLeftRightIcon" size={20} variant="solid" />
            <span className="text-sm font-heading font-semibold">Live Demo</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-6">
            See AI Support in Action
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Watch how our AI handles real customer inquiries with speed, accuracy, and empathy
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Chat Interface */}
          <div className="lg:col-span-2">
            <div className="bg-card rounded-2xl border border-border shadow-lg overflow-hidden">
              {/* Chat Header */}
              <div className="bg-gradient-to-r from-primary to-secondary p-6 text-primary-foreground">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center">
                    <Icon name="ChatBubbleLeftRightIcon" size={24} className="text-accent-foreground" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-lg">RedLetterX AI Assistant</h3>
                    <div className="flex items-center space-x-2 text-sm opacity-90">
                      <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
                      <span>Online • Typically responds in 2 seconds</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="h-96 overflow-y-auto p-6 space-y-4 bg-muted/20">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] ${message.type === 'user' ? 'order-2' : 'order-1'}`}>
                      <div
                        className={`rounded-2xl p-4 ${
                          message.type === 'user' ?'bg-accent text-accent-foreground' :'bg-card border border-border text-foreground'
                        }`}
                      >
                        <p className="text-sm leading-relaxed">{message.content}</p>
                      </div>
                      <div className={`text-xs text-muted-foreground mt-1 ${message.type === 'user' ? 'text-right' : 'text-left'}`}>
                        {message.timestamp}
                      </div>
                    </div>
                  </div>
                ))}

                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-card border border-border rounded-2xl p-4">
                      <div className="flex space-x-2">
                        <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Chat Input */}
              <div className="p-6 border-t border-border bg-card">
                <div className="flex items-center space-x-4">
                  <input
                    type="text"
                    placeholder="Type your message..."
                    className="flex-1 px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-foreground"
                    disabled
                  />
                  <button className="px-6 py-3 bg-accent text-accent-foreground font-heading font-semibold rounded-lg hover:bg-accent/90 transition-colors duration-300 flex items-center space-x-2" disabled>
                    <Icon name="PaperAirplaneIcon" size={20} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="space-y-6">
            <div className="bg-card rounded-xl p-6 border border-border">
              <h3 className="text-lg font-heading font-bold text-foreground mb-6">Real-Time Performance</h3>
              <div className="space-y-4">
                {performanceMetrics.map((metric, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Icon name={metric.icon as any} size={20} className={metric.color} />
                      <span className="text-sm font-heading font-semibold text-foreground">{metric.label}</span>
                    </div>
                    <span className={`text-lg font-heading font-bold ${metric.color}`}>{metric.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-accent/10 to-success/10 rounded-xl p-6 border border-accent/20">
              <div className="flex items-center space-x-3 mb-4">
                <Icon name="SparklesIcon" size={24} className="text-accent" variant="solid" />
                <h4 className="font-heading font-bold text-foreground">AI Capabilities</h4>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircleIcon" size={16} className="text-success mt-1" variant="solid" />
                  <span className="text-sm text-foreground">Natural language understanding</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircleIcon" size={16} className="text-success mt-1" variant="solid" />
                  <span className="text-sm text-foreground">Context-aware responses</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircleIcon" size={16} className="text-success mt-1" variant="solid" />
                  <span className="text-sm text-foreground">Multi-language support</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircleIcon" size={16} className="text-success mt-1" variant="solid" />
                  <span className="text-sm text-foreground">Sentiment analysis</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircleIcon" size={16} className="text-success mt-1" variant="solid" />
                  <span className="text-sm text-foreground">Intelligent escalation</span>
                </li>
              </ul>
            </div>

            <button className="w-full px-6 py-4 bg-accent text-accent-foreground font-heading font-semibold rounded-lg hover:bg-accent/90 hover:shadow-elevated transition-all duration-300 flex items-center justify-center space-x-2">
              <span>Try Interactive Demo</span>
              <Icon name="ArrowRightIcon" size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChatbotDemo;